import React, { useEffect } from 'react';

/**
 * Employee actions popover — the 9-tile coloured-icon grid + "Record as leaver"
 * footer (Phase 12). Used by the client dashboard (per-row ⋯ trigger) and the
 * employee profile (hero "Actions ▾" button).
 *
 * Self-contains dismissal: closes on outside click and Escape. The parent
 * decides what each action does via {@link onSelectAction}; this component
 * stays UI-only and doesn't know about modals or routes.
 *
 * @param {Object} props
 * @param {Object} props.emp - Employee object (id, first_name, last_name).
 * @param {{top:number,bottom:number,left:number,right:number}} props.anchorRect
 *   Bounding rect of the trigger button, in viewport coordinates.
 * @param {Array} [props.sickRecords] - Used to label the Sick Leave tile.
 * @param {() => void} props.onClose
 * @param {(actionId: string, emp: Object) => void} props.onSelectAction
 *   Called with one of:
 *     addition, deduction, bank_details, address, personal_details, pension, parental_leave,
 *     sick, unpaid_leave, contractual_change, record_leaver
 */
export default function EmployeeActionsPopover({ emp, anchorRect, sickRecords = [], onClose, onSelectAction }) {
  useEffect(() => {
    const onDoc = (e) => {
      if (!e.target.closest('[data-actions-popover]') && !e.target.closest('[data-actions-trigger]')) {
        onClose();
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!emp || !anchorRect) return null;

  const empName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'employee';
  const popoverWidth = Math.min(760, window.innerWidth - 32);
  const estHeight = 480;
  let top = anchorRect.bottom + 8;
  const left = Math.min(window.innerWidth - popoverWidth - 16, Math.max(16, anchorRect.right - popoverWidth));
  if (top + estHeight > window.innerHeight && anchorRect.top > estHeight + 16) {
    top = anchorRect.top - estHeight - 8;
  }
  const hasSick = !!sickRecords.find(r => r.employee_id === emp.id);

  const svg = (path) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{path}</svg>
  );
  const select = (id) => () => onSelectAction(id, emp);

  const actions = [
    { id: 'addition',           label: 'Addition',           hint: 'Bonus, overtime, etc',           bg: '#ecfdf5', stroke: '#10b981', glyph: <span className="text-[16px] font-bold leading-none">+</span> },
    { id: 'deduction',          label: 'Deduction',          hint: 'Loan, overpayment, etc',         bg: '#fff7ed', stroke: '#f97316', glyph: <span className="text-[18px] font-bold leading-none">−</span> },
    { id: 'bank_details',       label: 'Bank Details',       hint: 'Update account info',            bg: '#f0f9ff', stroke: '#0ea5e9', glyph: svg(<><rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10h18M7 15h2m4 0h4"/></>) },
    { id: 'address',            label: 'Address',            hint: 'Update home address',            bg: '#f0fdfa', stroke: '#14b8a6', glyph: svg(<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>) },
    { id: 'personal_details',   label: 'Personal Details',   hint: 'Name, DOB, gender, NI',          bg: '#f0fdf4', stroke: '#22c55e', glyph: svg(<><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"/></>) },
    { id: 'pension',            label: 'Pension',            hint: 'Edit contributions',             bg: '#faf5ff', stroke: '#a855f7', glyph: svg(<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>) },
    { id: 'parental_leave',     label: 'Parental Leave',     hint: 'Maternity, paternity, etc',      bg: '#fdf2f8', stroke: '#ec4899', glyph: svg(<><circle cx="9" cy="7" r="3"/><circle cx="17" cy="9" r="2"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M19 21v-1a3 3 0 0 0-2-2.83"/></>) },
    { id: 'sick',               label: hasSick ? 'Update Sick Leave' : 'Sick Leave', hint: hasSick ? 'Update absence or mark returned' : 'Record absence / SSP', bg: '#fffbeb', stroke: '#f59e0b', glyph: svg(<><circle cx="12" cy="12" r="4"/><path d="M12 4v1M12 19v1M4 12h1M19 12h1M6.3 6.3l.7.7M17 17l.7.7M6.3 17.7l.7-.7M17 7l.7-.7"/></>) },
    { id: 'unpaid_leave',       label: 'Unpaid Leave',       hint: 'Sabbatical, unpaid time off',    bg: '#fefce8', stroke: '#eab308', glyph: svg(<><circle cx="12" cy="12" r="9"/><line x1="5" y1="5" x2="19" y2="19"/></>) },
    { id: 'contractual_change', label: 'Contractual Change', hint: 'FT/PT, hours, salary',           bg: '#eef2ff', stroke: '#6366f1', glyph: svg(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="m17 14-5 5-3-3"/></>) },
  ];

  return (
    <div
      data-actions-popover
      role="menu"
      aria-label={`Actions for ${empName}`}
      className="fixed bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 shadow-2xl z-50"
      style={{ top, left, width: popoverWidth }}
    >
      <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-brand-50 dark:from-brand-900/30 to-transparent rounded-t-xl flex items-center justify-between">
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-brand-700 dark:text-brand-300">Actions for</p>
          <p className="text-[14px] font-bold text-ink dark:text-gray-100 leading-tight mt-0.5">{empName}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1" aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>
      </div>
      <div className="p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {actions.map(action => (
          <button
            key={action.id}
            onClick={select(action.id)}
            className="flex items-start gap-3 p-3 rounded-lg ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-brand-300 hover:shadow-sm text-left bg-white dark:bg-gray-900"
          >
            <span className="shrink-0 grid place-items-center w-9 h-9 rounded-full" style={{ background: action.bg, color: action.stroke }}>
              {action.glyph}
            </span>
            <div className="min-w-0">
              <p className="text-[13.5px] font-bold text-ink dark:text-gray-100 leading-tight">{action.label}</p>
              <p className="text-[11.5px] text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{action.hint}</p>
            </div>
          </button>
        ))}
      </div>
      <div className="px-3 py-3 border-t border-gray-100 dark:border-gray-700 bg-rose-50/40 dark:bg-rose-900/20 rounded-b-xl">
        <button
          onClick={select('record_leaver')}
          className="w-full flex items-center gap-3 p-3 rounded-lg ring-1 ring-rose-200 dark:ring-rose-800 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition text-left"
        >
          <span className="shrink-0 grid place-items-center w-9 h-9 rounded-full bg-rose-100 dark:bg-rose-900/40" style={{ color: '#e11d48' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13.5px] font-bold text-rose-700 dark:text-rose-300 leading-tight">Record as leaver</p>
            <p className="text-[11.5px] text-gray-500 dark:text-gray-400 mt-0.5">Resignation, dismissal, end of contract…</p>
          </div>
          <svg className="text-rose-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>
  );
}
